import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import AdminLayout from '../Componets/AdminLayout';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function EditSubject() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const focusInputRef = useRef(null);

  const [value, setValue] = useState({
    name: '',
    subjectCode: '',
    subjectObjectiveIds: [],
  });

  const [allObjectives, setAllObjectives] = useState([]); // For Select options
  const [selectedObjectives, setSelectedObjectives] = useState([]); // For multi-select value
  const [newObjectiveName, setNewObjectiveName] = useState('');
  const [loading, setLoading] = useState(false);
  const currentPath = location?.pathname || '';

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (role !== 'ADMIN') {
      navigate('/');
    }
    if (focusInputRef.current) focusInputRef.current.focus();
  }, [navigate]);

  // Fetch subject details & prefill
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!id) return;

    axios
      .get(`${apiUrl}/api/subject/${id}`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const subject = res.data;
        setValue({
          name: subject.name,
          subjectCode: subject.subjectCode,
          subjectObjectiveIds: subject.subjectObjectives.map((o) => o.id),
        });
        // Preselect objectives for react-select
        setSelectedObjectives(
          subject.subjectObjectives.map((o) => ({
            value: o.id,
            label: o.objectiveName,
          }))
        );
      })
      .catch((err) => {
        if (err.response?.status === 401) navigate('/');
        console.error(err);
      });
  }, [apiUrl, id, navigate]);

  // Fetch all objectives for options
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${apiUrl}/api/subject-objective/`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const opts = res.data.subjectObjectives.map((obj) => ({
          value: obj.id,
          label: obj.objectiveName,
        }));
        setAllObjectives(opts);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [apiUrl]);

  // Add new objective API call
  const addNewObjective = () => {
    if (!newObjectiveName.trim())
      return toast.error('Objective name cannot be empty');

    const token = localStorage.getItem('token');
    axios
      .post(
        `${apiUrl}/api/subject-objective/`,
        { objectiveName: newObjectiveName },
        {
          headers: { Authorization: token },
        }
      )
      .then((res) => {
        const newObj = res.data; // Assuming API returns created objective with id and objectiveName
        const newOption = { value: newObj.id, label: newObj.objectiveName };
        setAllObjectives((prev) => [...prev, newOption]);
        setSelectedObjectives((prev) => [...prev, newOption]);
        setNewObjectiveName('');
        toast.success('Objective added successfully');
      })
      .catch((err) => {
        toast.error('Failed to add objective');
        console.error(err);
      });
  };

  // On form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('token');
    const data = {
      name: value.name,
      subjectCode: value.subjectCode,
      subjectObjectiveIds: selectedObjectives.map((o) => o.value),
    };

    axios
      .put(`${apiUrl}/api/subject/${id}`, data, {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      })
      .then((res) => {
        console.log(res);
        toast.success('Change Subject information successfully');
        setLoading(false);

        if (currentPath === '/EditSubject') {
          const timeOutDuration = 3000;
          const timeoutId = setTimeout(() => {
            if (window.location.pathname === '/EditSubject') {
              navigate(-1);
            }
          }, timeOutDuration);
          return () => clearTimeout(timeoutId);
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Failed to update subject');
        setLoading(false);
      });
  };

  return (
    <AdminLayout ac2="active" dr="true">
      <main id="main" className="main">
        <div className="pagetitle">
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/AdminHome">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/ListSubject">View Subject</Link>
              </li>
              <li className="breadcrumb-item active">Edit Subject</li>
            </ol>
          </nav>
        </div>

        <section className="section col-lg-12">
          <div className="row">
            <div>
              <div className="card">
                <div className="card-body pt-5">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-6">
                        <div className="form-floating mb-2">
                          <input
                            ref={focusInputRef}
                            className="form-control"
                            type="text"
                            placeholder="Name"
                            value={value.name}
                            onChange={(e) =>
                              setValue({ ...value, name: e.target.value })
                            }
                            required
                          />
                          <label>
                            Subject Name <span className="text-danger">*</span>
                          </label>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="form-floating mb-2">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Subject Code"
                            value={value.subjectCode}
                            onChange={(e) =>
                              setValue({
                                ...value,
                                subjectCode: e.target.value,
                              })
                            }
                            required
                          />
                          <label>
                            Subject Code <span className="text-danger">*</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* React Select for Objectives */}
                    <div className="mb-3">
                      <Select
                        isMulti
                        options={allObjectives}
                        value={selectedObjectives}
                        onChange={setSelectedObjectives}
                        placeholder="Select Subject Objectives"
                      />
                    </div>

                    <div className="row mb-3">
                      <div className="col-sm-12 pt-4 text-end">
                        <button
                          type="button"
                          className="btn btn-primary px-3"
                          onClick={() => navigate(-1)}
                        >
                          <i className="bi bi-arrow-bar-left"></i> Back
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary px-4 mx-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>{' '}
                              Saving...
                            </>
                          ) : (
                            <>Save</>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>

                  <ToastContainer
                    position="top-center"
                    style={{ width: 'auto' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </AdminLayout>
  );
}
